import type {
  ImportLedgerAccountsPayload,
  ImportLedgerAccountsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  zodImportLedgerAccountsPayloadSchema,
  zodImportLedgerAccountsResponseSchema,
} from './validations';

function toWireColumnNamesMap(
  columnNamesMap: ImportLedgerAccountsPayload['columnNamesMap'],
) {
  return columnNamesMap.map(({ expected, nameInFile }) => ({
    expected,
    name_in_file: nameInFile,
  }));
}

/**
 * Imports a complete chart of accounts from a CSV or XLSX file.
 *
 * @param payload - File and import settings sent as multipart form data.
 * @param options - Optional request options, such as an abort signal.
 * @returns Aggregate import result counts.
 * @throws Error if the success response does not match the API contract.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts
 */
export const importLedgerAccounts = async (
  payload: ImportLedgerAccountsPayload,
  options?: KyRequestOptions,
) => {
  const {
    applyToAllCompaniesInAccount,
    columnNamesMap,
    companyIds,
    file,
    isDryRun,
    validFrom,
  } = zodImportLedgerAccountsPayloadSchema.parse(payload);

  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'column_names_map',
    JSON.stringify(toWireColumnNamesMap(columnNamesMap)),
  );

  for (const companyId of companyIds ?? []) {
    formData.append('company_ids', companyId);
  }

  formData.append(
    'apply_to_all_companies_in_account',
    String(applyToAllCompaniesInAccount),
  );

  if (validFrom) {
    formData.append('valid_from', validFrom);
  }

  formData.append('is_dry_run', String(isDryRun));

  const response = await vestigasApi
    .post(ENDPOINT.LEDGER_ACCOUNT.IMPORT(), {
      ...options,
      body: formData,
    })
    .json();

  const parsed = zodImportLedgerAccountsResponseSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'import-ledger-accounts',
      message: 'import ledger accounts response has validation errors',
    });

    throw new Error(
      'Import ledger accounts response did not match the expected shape',
    );
  }

  return parsed.data satisfies ImportLedgerAccountsResponse;
};
